import {
  useQuery,
  useInfiniteQuery,
  UseQueryOptions,
  QueryFunctionContext,
} from '@tanstack/react-query';

import AudiencesService, {
  AudiencesIndexParams,
  AudiencesIndexResponse,
} from '../../services/audiences';

export const AUDIENCES_QUERY_KEY = ['audiences'];

/**
 * Fetch audiences from mojo
 *
 * @param params - Optional query parameters for filtering/pagination
 * @param options - Optional React Query options
 * @returns Query result with audiences data
 */

export const useAudiencesQuery = (
  params?: AudiencesIndexParams,
  options?: Omit<
    UseQueryOptions<
      AudiencesIndexResponse,
      Error,
      AudiencesIndexResponse,
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const query = useQuery<
    AudiencesIndexResponse,
    Error,
    AudiencesIndexResponse,
    string[]
  >({
    queryKey: [...AUDIENCES_QUERY_KEY, JSON.stringify(params)],

    queryFn: async () => {
      const response = await new AudiencesService().fetchAll(params);
      return response.data;
    },

    meta: { persist: false },
    ...options,
  });

  return query;
};

export const useAudiencesInfiniteQuery = (
  params?: Omit<AudiencesIndexParams, 'page'>
) => {
  return useInfiniteQuery<
    AudiencesIndexResponse,
    Error,
    AudiencesIndexResponse,
    string[]
  >({
    queryKey: [...AUDIENCES_QUERY_KEY, 'infinite', JSON.stringify(params)],
    queryFn: async ({ pageParam }: QueryFunctionContext<string[], number>) => {
      const response = await new AudiencesService().fetchAll({
        ...params,
        page: (pageParam as number) || 1,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, total_pages } = lastPage.meta;
      return current_page < total_pages ? current_page + 1 : undefined;
    },
    meta: { persist: false },
  });
};
